/**
 * Connect an assistant.
 *
 * The steps are not written here — they come from `GET /api/connect`, which is
 * the same data the `connect_info` MCP tool and the extension's Connect view
 * render. One source, so a change to how claude.ai's connector screen works is
 * one edit in the Worker rather than three in three clients.
 */

import { useState, type ReactNode } from "react";
import Accordion from "react-bootstrap/Accordion";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { Link } from "react-router";
import { api } from "../api";
import { CopyButton, ErrorAlert, Skeleton, SkeletonRows } from "../components/ui";
import { useAsync } from "../useAsync";
import { useSession } from "../useSession";

/** Turns the one part of a step that is a value — a URL or a command — into code. */
function Step({ text }: { text: string }): ReactNode {
  const patterns = [/(https?:\/\/\S+)/, /(claude mcp add [^\n]+)/, /(\{[^\n]*"mcpServers"[^\n]*\})/];
  for (const pattern of patterns) {
    const match = pattern.exec(text);
    if (!match || match.index === undefined) continue;
    const value = match[1] ?? "";
    return (
      <>
        {text.slice(0, match.index)}
        <code className="emb-url">{value}</code>
        {text.slice(match.index + value.length)}
      </>
    );
  }
  return <>{text}</>;
}

export function Connect(): ReactNode {
  const { account } = useSession();
  const connect = useAsync(() => api.connect(), []);
  const [open, setOpen] = useState<string>("0");

  return (
    <Container style={{ maxWidth: "60rem" }}>
      <p className="emb-eyebrow mb-2">Connect</p>
      <h1 className="h3 mb-2">Point your assistant at one URL</h1>
      <p className="emb-muted mb-4" style={{ maxWidth: "44rem" }}>
        This endpoint is an MCP server and an OAuth 2.1 protected resource. Your client registers itself, sends you here
        to approve, and from then on acts as your account until you revoke it on the{" "}
        <Link to="/account">Account</Link> page.
      </p>

      {connect.error ? (
        <ErrorAlert error={connect.error} onRetry={() => void connect.reload()} />
      ) : (
        <>
          <Card className="mb-4">
            <Card.Body>
              <p className="emb-eyebrow mb-2">MCP server URL</p>
              {connect.loading || !connect.data ? (
                <Skeleton height="3rem" />
              ) : (
                <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2 emb-sunken px-3 py-3">
                  <code className="emb-url flex-grow-1 fs-6">{connect.data.mcpUrl}</code>
                  <CopyButton value={connect.data.mcpUrl} label="Copy URL" variant="primary" />
                </div>
              )}
              <p className="emb-muted small mb-0 mt-3">
                Transport: streamable HTTP (POST only). Authentication: OAuth 2.1 with PKCE — dynamic client
                registration is open, so there is no app to create.
                {connect.data ? (
                  <>
                    {" "}
                    Authorization server: <code className="emb-mono">{connect.data.authorizationServer}</code>.
                  </>
                ) : null}
              </p>
            </Card.Body>
          </Card>

          <Row className="g-4">
            <Col lg={8}>
              <Card>
                <Card.Header>Step by step, by client</Card.Header>
                <Card.Body className="p-0">
                  {connect.loading || !connect.data ? (
                    <div className="p-4">
                      <SkeletonRows rows={5} />
                    </div>
                  ) : (
                    <Accordion activeKey={open} onSelect={(key) => setOpen(typeof key === "string" ? key : "")} flush>
                      {connect.data.clients.map((client, index) => (
                        <Accordion.Item eventKey={String(index)} key={client.name}>
                          <Accordion.Header>{client.name}</Accordion.Header>
                          <Accordion.Body>
                            <ol className="mb-0 ps-3 d-flex flex-column gap-2">
                              {client.steps.map((step, stepIndex) => (
                                <li key={stepIndex}>
                                  <Step text={step} />
                                </li>
                              ))}
                            </ol>
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <Card className="mb-4">
                <Card.Body>
                  <h2 className="h6 mb-2">Link a browser first</h2>
                  <p className="emb-muted small mb-0">
                    An assistant with no linked browser can read and change your configuration, but every tool that
                    looks at a page will answer <em>“No linked browser is connected.”</em> Install the extension, open
                    its side panel and choose <strong>Link this browser</strong>.
                    {account ? (
                      <>
                        {" "}
                        Your linked browsers are on the <Link to="/account">Account</Link> page.
                      </>
                    ) : null}
                  </p>
                </Card.Body>
              </Card>

              <Card>
                <Card.Body>
                  <h2 className="h6 mb-2">What a connection may do</h2>
                  <ul className="emb-muted small mb-0 ps-3 d-flex flex-column gap-1">
                    <li>On request, read your current browser tab — structure, text, screenshots — and list open tabs.</li>
                    <li>On request, act in that tab as you — click, type, navigate, scroll, change what it shows.</li>
                    <li>Change this extension's configuration, and preview changes in your tab.</li>
                    <li>Upload and run logic modules.</li>
                  </ul>
                  <p className="emb-muted small mb-0 mt-3">
                    It cannot read your captured data: capture logs live in the extension, never here. Every call is
                    recorded and shown live in the extension's Activity view.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
}
